import type { FieldValue } from '@pigello/pigello-matrix';
import { DateTime, Settings } from 'luxon';

Settings.defaultLocale = 'sv';

export const toReadableDate = (date?: FieldValue<string>) => {
  if (!date) return '-';

  return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
};

// Converts a (possibly UTC) ISO datetime into the calendar date as seen in the
// local time zone. Using a naive `value.split('T')[0]` truncates in UTC, which
// shifts the date a day backwards for zones ahead of UTC (e.g. a 2026-06-04
// 00:00 access time in Stockholm is stored as 2026-06-03T22:00:00Z).
export const toLocalISODate = (
  date?: FieldValue<string>
): string | undefined => {
  if (!date) return undefined;

  return DateTime.fromISO(date).toISODate() ?? undefined;
};

export const toReadableDateTime = (date: FieldValue<string>) => {
  if (!date) {
    return '-';
  }

  return DateTime.fromISO(date).setZone('local').toFormat('yyyy-MM-dd HH:mm');
};

export const toReadableTime = (date: FieldValue<string>) => {
  if (!date) {
    return '-';
  }

  return DateTime.fromISO(date).setZone('local').toFormat('HH:mm');
};

export const toFileNameDateTime = (date: FieldValue<string>) => {
  if (!date) {
    return 'unknown-time';
  }

  return DateTime.fromISO(date).setZone('local').toFormat('yyyy-MM-dd_HH-mm');
};

export function toReadableMonths(months: FieldValue<number>) {
  if (!months) {
    return '-';
  }

  return `${months} ${months === 1 ? 'månad' : 'månader'}`;
}

export function toReadableYearsAndMonths(months: FieldValue<number>) {
  if (!months) {
    return '-';
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    return `${remainingMonths} ${remainingMonths === 1 ? 'månad' : 'månader'}`;
  }

  if (remainingMonths === 0) {
    return `${years} år`;
  }

  return `${years} år, ${remainingMonths} ${remainingMonths === 1 ? 'månad' : 'månader'}`;
}

export function monthIndexToString(monthIndex: number) {
  switch (monthIndex) {
    case 1:
      return 'Januari';
    case 2:
      return 'Februari';
    case 3:
      return 'Mars';
    case 4:
      return 'April';
    case 5:
      return 'Maj';
    case 6:
      return 'Juni';
    case 7:
      return 'Juli';
    case 8:
      return 'Augusti';
    case 9:
      return 'September';
    case 10:
      return 'Oktober';
    case 11:
      return 'November';
    case 12:
      return 'December';
    default:
      return '';
  }
}

/**
 * Human-friendly datetime relative to `now`:
 * "Idag kl 09:00", "Imorgon kl 12:30", otherwise "Måndag 9 juni kl 13:00"
 * (with the year appended when it differs from the current one).
 */
export const toRelativeDayDateTime = (
  date: FieldValue<string>,
  now: DateTime = DateTime.now()
) => {
  if (!date) return '-';

  const dt = DateTime.fromISO(date).setZone('local');
  const time = dt.toFormat('HH:mm');

  if (dt.hasSame(now, 'day')) return `Idag kl ${time}`;
  if (dt.hasSame(now.plus({ days: 1 }), 'day')) return `Imorgon kl ${time}`;

  const day = dt.toFormat(
    dt.hasSame(now, 'year') ? 'cccc d MMMM' : 'cccc d MMMM yyyy'
  );
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} kl ${time}`;
};
